import * as React from 'react'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Group from 'components/utils/Group'
import Typography from 'LuxKit/Typography/Typography'
import FormatCurrency from '../FormatCurrency'
import type { FilterPanelCheckItem } from './FilterPanelCheckboxGroup'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { useIntl } from 'react-intl'

interface Props {
  name: string
  item: FilterPanelCheckItem
  defaultChecked?: boolean
  checked?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  titlecase?: boolean
  maxValue?: number
  loadingValue?: boolean
  hideCheckbox?: boolean
}

function FilterPanelCheckboxGroupItem(props: Props) {
  const {
    onChange,
    name,
    item,
    defaultChecked,
    checked,
    titlecase = false,
    maxValue = 1000,
    loadingValue,
    hideCheckbox = false,
  } = props

  const intl = useIntl()

  const showLabelOnly = item.price === undefined && (typeof item.count !== 'number' || item.hideCount)
  const showCount = typeof item.count === 'number' && item.price === undefined && !item.hideCount
  const showPrice = item.price !== undefined

  const exceedsMaxCountLabel = intl.formatMessage(
    {
      id: 'fhC-ma__babrGtmtNhVx',
      defaultMessage: '({maxCount}+)',
      description: 'Filter checkbox item count when it exceeds the maximum displayable value',
    },
    { maxCount: maxValue },
  )
  const countLabel = intl.formatMessage(
    {
      id: 'AQj1DTTPdmt-bGxPXG7x',
      defaultMessage: '({count})',
      description: 'Parenthesised count of matching results shown next to a filter checkbox option',
    },
    { count: item.count },
  )

  return (
    <CheckboxInput
      key={item.value}
      name={name}
      value={item.value}
      size="medium"
      onChange={onChange}
      defaultChecked={defaultChecked}
      checked={item.checked ?? checked}
      disabled={item.disabled}
      hidden={item.hidden}
      data-carrier={item.value}
      hideCheckbox={hideCheckbox}
    >
      {showCount && (
        <Group direction="horizontal" horizontalAlign="space-between">
          {item.label}
          {loadingValue && <TextLoadingBox width="3ch" />}
          {!loadingValue && <span>{item.count! > maxValue ? exceedsMaxCountLabel : countLabel}</span>}
        </Group>
      )}

      {showPrice && (
        <Group direction="horizontal" horizontalAlign="space-between" gap="0 4">
          {item.label}

          <span>
            <FormatCurrency
              value={item.price!}
              format="roundedDollar"
            />
          </span>
        </Group>
      )}

      {showLabelOnly && (
        <Typography format={titlecase ? 'titlecase' : undefined} as="span">
          {item.label}
        </Typography>
      )}
    </CheckboxInput>
  )
}

export default FilterPanelCheckboxGroupItem
